<template>
  <v-dialog width="80%" v-model="displayed" scrollable v-if="displayed">
    <v-card>
      <v-toolbar color="#eee" height="48px" flat>
        <v-toolbar-title>
          {{ $t('organization.delegating-orgs.title', { name: organization.name }) }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs v-model="activeTab" grow>
        <v-tab>
          {{ $t('organization.delegating-orgs.tab-linked') }}
          <v-chip x-small class="ml-2">{{ linkedOrgs.length }}</v-chip>
        </v-tab>
        <v-tab>
          {{ $t('organization.delegating-orgs.tab-add') }}
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text class="pa-0 delegating-orgs-modal-body">
        <v-tabs-items v-model="activeTab" class="fill-height">

          <!-- LINKED TAB -->
          <v-tab-item class="fill-height" :transition="false" :reverse-transition="false">
            <div class="pa-4 d-flex flex-column fill-height">
              <div class="d-flex align-center mb-3 flex-shrink-0">
                <v-text-field
                    v-model="linkedSearch"
                    :placeholder="$t('search')"
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    dense
                    outlined
                    clearable
                    style="max-width: 320px;"
                />
                <v-spacer />
                <v-btn
                    small
                    color="error"
                    :disabled="!linkedSelected.length || saving"
                    :loading="saving"
                    @click="bulkRemove"
                >
                  <v-icon left small>mdi-delete-outline</v-icon>
                  {{ $t('organization.delegating-orgs.remove-selected', { count: linkedSelected.length }) }}
                </v-btn>
              </div>

              <div v-if="linkedOrgs.length === 0" class="text--secondary text-center py-8 flex-grow-1">
                {{ $t('organization.delegating-orgs.empty') }}
              </div>

              <div v-else class="delegating-orgs-modal-list">
                <v-data-table
                    v-model="linkedSelected"
                    :items="filteredLinked"
                    :headers="linkedHeaders"
                    item-key="id"
                    show-select
                    dense
                    :items-per-page="-1"
                    hide-default-footer
                    :loading="loadingLinked"
                >
                  <template #item.scope="{item}">
                    <v-chip x-small :color="delegationScopeColor(item)" outlined>
                      {{ $t('organization.delegating-orgs.scope-values.' + delegationScopeKey(item)) }}
                    </v-chip>
                  </template>
                  <template #item.actions="{item}">
                    <v-btn icon small @click="removeOne(item.id)">
                      <v-icon small color="red">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </div>
          </v-tab-item>

          <!-- ADD TAB -->
          <v-tab-item class="fill-height" :transition="false" :reverse-transition="false">
            <div class="pa-4 d-flex flex-column fill-height">
              <v-alert type="info" text dense border="left" class="mb-3 flex-shrink-0 text-caption">
                {{ $t('organization.delegating-orgs.auto-invoicing-warning') }}
              </v-alert>
              <div class="d-flex align-center mb-3 flex-shrink-0">
                <v-text-field
                    v-model="addSearch"
                    :placeholder="$t('organization.delegating-orgs.add-placeholder')"
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    dense
                    outlined
                    clearable
                    style="max-width: 320px;"
                    @input="onAddSearchInput"
                />
                <v-spacer />
                <v-btn
                    small
                    color="primary"
                    :disabled="!addSelected.length || saving"
                    :loading="saving"
                    @click="bulkAdd"
                >
                  <v-icon left small>mdi-account-multiple-plus-outline</v-icon>
                  {{ $t('organization.delegating-orgs.add-selected', { count: addSelected.length }) }}
                </v-btn>
              </div>

              <div v-if="!loadingAvailable && availableOrgs.length === 0" class="text--secondary text-center py-8 flex-grow-1">
                {{ $t('organization.delegating-orgs.no-candidates') }}
              </div>

              <div v-else class="delegating-orgs-modal-list">
                <v-data-table
                    v-model="addSelected"
                    :items="availableOrgs"
                    :headers="availableHeaders"
                    item-key="id"
                    show-select
                    dense
                    :items-per-page="-1"
                    hide-default-footer
                    :loading="loadingAvailable"
                />
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn small @click="close">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import OrganizationRepository from "@repository/OrganizationRepository"

const ADD_SEARCH_LIMIT = 10000
const LINKED_LIMIT = 10000

export default {
  props: {
    organization: { type: Object, required: true },
  },

  data() {
    return {
      displayed: false,
      activeTab: 0,
      saving: false,

      // Linked
      linkedOrgs: [],
      linkedSelected: [],
      linkedSearch: '',
      loadingLinked: false,

      // Add
      addSearch: '',
      addSearchTimer: null,
      availableOrgs: [],
      availableSelected: [],
      loadingAvailable: false,
      lastAddSearchLoaded: null,
    }
  },

  computed: {
    linkedHeaders() {
      return [
        { text: this.$t('name'), value: 'name', sortable: true },
        { text: this.$t('account-number'), value: 'accountNumber', sortable: true },
        { text: this.$t('organization.delegating-orgs.scope'), value: 'scope', sortable: false },
        { text: '', value: 'actions', sortable: false, width: 60, align: 'end' },
      ]
    },

    availableHeaders() {
      return [
        { text: this.$t('name'), value: 'name', sortable: true },
        { text: this.$t('account-number'), value: 'accountNumber', sortable: true },
      ]
    },

    addSelected: {
      get() { return this.availableSelected },
      set(v) { this.availableSelected = v },
    },

    filteredLinked() {
      const q = (this.linkedSearch || '').trim().toLowerCase()
      if (!q) return this.linkedOrgs
      return this.linkedOrgs.filter(o =>
          (o.name || '').toLowerCase().includes(q)
          || (o.accountNumber || '').toLowerCase().includes(q)
      )
    },

    linkedIds() {
      return new Set(this.linkedOrgs.map(o => o.id))
    },
  },

  methods: {
    delegationScopeKey(item) {
      const myId = this.organization?.id
      // Each linked org points at this organization through one or both typology fields.
      // Fall back to the legacy embed for pre-backfill orgs (then scope is 'all').
      const subToMe = item.subscriptionInvoiceRecipient?.id === myId
      const usgToMe = item.usageInvoiceRecipient?.id === myId
      if (subToMe && usgToMe) return 'all'
      if (subToMe) return 'subscription'
      if (usgToMe) return 'usage'
      return 'all'
    },

    delegationScopeColor(item) {
      const key = this.delegationScopeKey(item)
      return key === 'all' ? 'primary' : 'grey darken-1'
    },

    open() {
      this.displayed = true
      this.activeTab = 0
      this.linkedSearch = ''
      this.linkedSelected = []
      this.addSearch = ''
      this.availableSelected = []
      this.availableOrgs = []
      this.lastAddSearchLoaded = null
      this.loadLinked()
    },

    close() {
      this.displayed = false
    },

    loadLinked() {
      this.loadingLinked = true
      return OrganizationRepository.getDelegatingOrganizations(this.organization.id, { limit: LINKED_LIMIT }).then(data => {
        const items = data.items || data || []
        this.linkedOrgs = items.map(o => ({
          id: o.id,
          name: o.name,
          accountNumber: o.accountNumber || null,
          // The scope chip (abonnements / consommations / tout) reads these embeds.
          subscriptionInvoiceRecipient: o.subscriptionInvoiceRecipient || null,
          usageInvoiceRecipient: o.usageInvoiceRecipient || null,
        }))
        this.linkedSelected = []
        this.loadingLinked = false
      }).catch(err => {
        this.loadingLinked = false
        this.notifyError(err)
        throw err
      })
    },

    loadAvailable(searchText) {
      const requested = searchText || ''
      this.loadingAvailable = true
      OrganizationRepository.search({
        searchText: requested,
        canBeInvoiceRecipient: false,
        limit: ADD_SEARCH_LIMIT,
      }, 'delegating-orgs-search').then(result => {
        const items = result.items || []
        const linked = this.linkedIds
        const selfId = this.organization.id
        this.availableOrgs = items
            .filter(o => o.id !== selfId && !linked.has(o.id))
            .map(o => ({
              id: o.id,
              name: o.name,
              accountNumber: o.accountNumber || null,
            }))
        this.lastAddSearchLoaded = requested
        this.loadingAvailable = false
      }).catch(err => {
        this.loadingAvailable = false
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          this.notifyError(err)
        }
      })
    },

    onAddSearchInput() {
      if (this.addSearchTimer) clearTimeout(this.addSearchTimer)
      this.addSearchTimer = setTimeout(() => {
        this.loadAvailable(this.addSearch)
      }, 300)
    },

    bulkAdd() {
      if (!this.availableSelected.length) return
      const add = this.availableSelected.map(o => o.id)
      const addedSet = new Set(add)
      this.saving = true
      OrganizationRepository.updateDelegatingOrganizations(this.organization.id, { add }).then(() => {
        this.saving = false
        this.notifySuccess('update-success')
        this.availableSelected = []
        this.availableOrgs = this.availableOrgs.filter(o => !addedSet.has(o.id))
        this.$emit('updated')
        this.loadLinked()
      }).catch(err => {
        this.saving = false
        this.notifyError(err)
      })
    },

    bulkRemove() {
      if (!this.linkedSelected.length) return
      const remove = this.linkedSelected.map(o => o.id)
      this.saving = true
      OrganizationRepository.updateDelegatingOrganizations(this.organization.id, { remove }).then(() => {
        this.saving = false
        this.notifySuccess('update-success')
        this.linkedSelected = []
        this.$emit('updated')
        this.loadLinked().then(() => {
          if (this.activeTab === 1 || this.availableOrgs.length > 0) {
            this.loadAvailable(this.addSearch)
          }
        }).catch(() => {})
      }).catch(err => {
        this.saving = false
        this.notifyError(err)
      })
    },

    removeOne(id) {
      this.saving = true
      OrganizationRepository.updateDelegatingOrganizations(this.organization.id, { remove: [id] }).then(() => {
        this.saving = false
        this.notifySuccess('update-success')
        this.$emit('updated')
        this.loadLinked().then(() => {
          if (this.activeTab === 1 || this.availableOrgs.length > 0) {
            this.loadAvailable(this.addSearch)
          }
        }).catch(() => {})
      }).catch(err => {
        this.saving = false
        this.notifyError(err)
      })
    },
  },

  watch: {
    activeTab(val) {
      if (val === 1) {
        const target = this.addSearch || ''
        if (this.lastAddSearchLoaded !== target) {
          if (this.addSearchTimer) clearTimeout(this.addSearchTimer)
          this.loadAvailable(target)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.delegating-orgs-modal-body {
  height: 65vh;
  overflow: hidden !important;
}
.delegating-orgs-modal-list {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
</style>
